import { Component } from '@theme/component';
import { removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  // Only clear the returned menu-button focus after pointer/touch interaction.
  // Keyboard and assistive-technology focus visibility is preserved.
  #lastInputWasPointer = false;
  #onPointerDown = () => { this.#lastInputWasPointer = true; };
  #onKeyDown = () => { this.#lastInputWasPointer = false; };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onNavigationLinkClick);
    this.refs.details.addEventListener('toggle', this.#onRootDetailsToggle);
    this.refs.menuDrawer.addEventListener('cancel', this.#onModalCancel);
    this.refs.menuDrawer.addEventListener('close', this.#onModalClose);
    this.refs.menuDrawer.addEventListener('click', this.#onBackdropClick);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#setupAnimatedElementListeners();
    // Restore a deterministic closed state across theme-editor reconnects.
    if (this.isOpen) this.#finishRootClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onNavigationLinkClick);
    this.refs.details.removeEventListener('toggle', this.#onRootDetailsToggle);
    this.refs.menuDrawer.removeEventListener('cancel', this.#onModalCancel);
    this.refs.menuDrawer.removeEventListener('close', this.#onModalClose);
    this.refs.menuDrawer.removeEventListener('click', this.#onBackdropClick);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#hideModal();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#finishRootClose();
  };

  /**
   * Navigation links must close the scroll-locking drawer before their
   * default navigation runs. This matters especially for same-page hash
   * links: the browser scrolls to the target without a full page reload,
   * so an open <details scroll-lock> would keep the page unscrollable.
   * Do not intercept the URL, preview-session query parameters, or hash.
   * @param {MouseEvent} event
   */
  #onNavigationLinkClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('.menu-drawer__navigation a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link) || !this.isOpen) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target === '_blank' || link.hasAttribute('download')) return;

    // Finish closing before native same-page hash navigation; a live modal
    // would otherwise leave the destination page inert.
    this.#finishRootClose();
  };

  /**
   * Promote the existing menu into the browser's native Top Layer.
   * No fixed-position containing block, z-index patch or sticky-header offset.
   * The open <dialog> is the only scroll-lock owner for this menu.
   */
  #showModal() {
    const modal = this.refs.menuDrawer;
    if (!(modal instanceof HTMLDialogElement) || modal.open) return;
    modal.showModal();
    lockScroll(modal);
    modal.scrollTop = 0;
  }

  #hideModal() {
    const modal = this.refs.menuDrawer;
    if (modal instanceof HTMLDialogElement && modal.open) modal.close();
    unlockScroll(modal);
  }

  #finishRootClose() {
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.querySelectorAll('details[open]').forEach(reset);
    reset(this.refs.details);
    removeTrapFocus();
    this.#hideModal();
  }

  #onRootDetailsToggle = () => {
    if (this.isOpen) this.#showModal();
    else this.#hideModal();
  };

  #onModalCancel = (event) => {
    // Escape closes root details and modal together.
    event.preventDefault();
    this.#finishRootClose();
  };

  #onModalClose = () => {
    // A native close event can arrive after a rapid close-and-reopen.
    // Do not unlock a *newly reopened* modal on a stale close event.
    if (this.refs.menuDrawer.open) return;
    unlockScroll(this.refs.menuDrawer);
    if (this.isOpen) this.#finishRootClose();

    // Native <dialog> may restore focus to the opening <summary> when closing.
    // On touch Safari this can leave a large visible outline around the icon.
    // Blur only after pointer-driven closes; never suppress keyboard focus rings.
    if (this.#lastInputWasPointer) {
      requestAnimationFrame(() => {
        const trigger = this.refs.details.querySelector(':scope > summary');
        if (!this.isOpen && document.activeElement === trigger) trigger.blur();
      });
    }
  };

  #onBackdropClick = (event) => {
    if (event.target !== this.refs.menuDrawer) return;
    const box = this.refs.menuDrawer.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right ||
        event.clientY < box.top || event.clientY > box.bottom) {
      this.#finishRootClose();
    }
  };

  #onPageShow = () => {
    if (this.isOpen) this.#finishRootClose();
    else this.#hideModal();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (!details.hasAttribute('open')) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }
      if (details === this.refs.details) return; // Native modal owns root focus.

      // Native <dialog> owns modal focus; nested details keep normal keyboard
      // navigation instead of registering a second document-wide focus trap.
      // (No nested trapFocus required.)
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (details === this.refs.details) {
      this.#finishRootClose();
      return;
    }

    // Keep the nested submenu exit animation, then return focus to its
    // summary. The outer native modal remains in charge of focus containment.
    const submenu = details.querySelector('.menu-drawer__submenu');
    onAnimationEnd(submenu || details, () => {
      reset(details);
      if (this.isOpen) details.querySelector('summary')?.focus({ preventScroll: true });
    }, { subtree: false });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
